import {
    type FC,
    type ReactNode,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useShopfrontDispatch } from "../../../_Bootstrap/hooks";
import { showDialog } from "../../../Actions/Dialogs/DialogActions";
import Button from "../../../Common/Buttons/Button";
import { GridOnTop } from "../../../Common/Forms/GridEditor/GridOnTop";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { type DashboardItemEditType, type ReportOptionType } from "./DashboardModify";
import { DashboardModifyItemDialog } from "./DashboardModifyItemDialog";
import { DashboardModifyItemForm } from "./DashboardModifyItemForm";

interface Props extends DashboardItemEditType {
    options: Array<ReportOptionType>;
    onChange: (report: DashboardItemEditType) => void;
    error: boolean;
}

const SMALL_WIDTH_THRESHOLD = 275;

export const DashboardModifyItem: FC<Props> = ({
    options,
    report,
    filterUser,
    onChange,
    error,
}) => {
    const dispatch = useShopfrontDispatch();

    const itemRef = useRef<HTMLDivElement>(null);

    const [ small, setSmall ] = useState(false);
    const [ editing, setEditing ] = useState(false);

    useEffect(() => {
        if(!itemRef.current) {
            return;
        }

        const resizeObserver = new ResizeObserver(entries => {
            if(!entries.length) {
                return;
            }

            const item = entries[0];

            setSmall(item.contentRect.width < SMALL_WIDTH_THRESHOLD);
        });

        resizeObserver.observe(itemRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const value = useMemo(() => {
        if(!report) {
            return null;
        }

        return options.find(option => option.value === report) ?? null;
    }, [ report, options ]);

    let content: ReactNode;

    if(small) {
        if(editing) {
            content = (
                <div>
                    <FontAwesomeLight icon="pencil" /> Currently Editing
                </div>
            );
        } else {
            content = (
                <Button
                    classes={[ "button-add" ]}
                    onClick={e => {
                        e.stopPropagation();

                        setEditing(true);

                        dispatch(showDialog(
                            "Edit Dashboard Item",
                            <DashboardModifyItemDialog
                                report={value}
                                filterUser={filterUser}
                                options={options}
                                onMountChange={setEditing}
                                onSubmit={(value) => {
                                    onChange(value);
                                }}
                            />,
                            "question"
                        ));
                    }}
                >
                    <FontAwesomeLight icon="pencil" />Edit Item
                </Button>
            );
        }
    } else {
        content = (
            <DashboardModifyItemForm
                options={options}
                report={value}
                setReport={(option: ReportOptionType) => {
                    onChange({
                        report: option.value,
                        filterUser,
                    });
                }}
                filterUser={filterUser}
                setFilterUser={filterUser => {
                    onChange({
                        filterUser,
                        report,
                    });
                }}
            />
        );
    }

    return (
        <div
            ref={itemRef}
            className={
                "relative flex flex-col justify-center items-center w-full h-full overflow-x-hidden overflow-y-auto " +
                "p-4 border " + (
                    error ?
                        "border-red-700 dark:border-red-300 bg-red-200 dark:bg-red-950" :
                        "border-black dark:border-neutral-500 bg-neutral-100 dark:bg-dark-700"
                )
            }
        >
            <GridOnTop className="flex flex-col justify-center items-center w-full m-4">
                {content}
            </GridOnTop>
        </div>
    );
};
